/**
 * My Apps Section — Custom Applications
 *
 * Displays user-defined external app links grouped by category.
 * Features: category tabs with kebab menu (rename/delete), MUI dialogs, inline app editing.
 */

import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, MoreHorizontal } from "lucide-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomAppsGrid from "./CustomAppsGrid";
import { ConfirmModal } from "../../shared/components/ui";
import type {
  UserCustomApp,
  CreateUserCustomAppDto,
  UserCustomAppCategory,
} from "../../types";
import {
  useMyCustomApps,
  useCreateCustomApp,
  useUpdateCustomApp,
  useDeleteCustomApp,
  useToggleCustomAppFavorite,
  useReorderCustomApps,
  useMyCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from "../../hooks";
import { AddEditCustomAppModal } from "./AddEditCustomAppModal";
import { reorderVisibleApps, sortAppsByOrder } from "../../utils/dashboardAppOrder";

const DEFAULT_CAT_COLOR = "#c00035";

// ---------------------------------------------------------------------------
// Create / Edit Category Modal (MUI Dialog)
// ---------------------------------------------------------------------------

interface CategoryFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Pass a category to edit it; omit for create mode */
  editCategory?: UserCustomAppCategory | null;
  onCreated?: (cat: UserCustomAppCategory) => void;
}

const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
  open,
  onClose,
  editCategory,
  onCreated,
}) => {
  const isEdit = !!editCategory;
  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_CAT_COLOR);
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();

  useEffect(() => {
    if (open) {
      setName(editCategory?.name ?? "");
      setColor(editCategory?.color ?? DEFAULT_CAT_COLOR);
    }
  }, [open, editCategory]);

  const handleClose = () => {
    setName("");
    setColor(DEFAULT_CAT_COLOR);
    onClose();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSave = () => {
    if (!name.trim()) return;
    if (isEdit && editCategory) {
      updateMutation.mutate(
        {
          id: editCategory.id,
          data: { name: name.trim(), color: color || undefined },
        },
        { onSuccess: handleClose },
      );
    } else {
      createMutation.mutate(
        { name: name.trim(), color: color || undefined },
        {
          onSuccess: (cat) => {
            onCreated?.(cat);
            setName("");
            setColor(DEFAULT_CAT_COLOR);
          },
        },
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        {isEdit ? "Rename category" : "New category"}
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <TextField
          autoFocus
          label="Category name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
          }}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 font-medium">Color</label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-9 w-16 rounded border border-gray-300 cursor-pointer p-0.5"
          />
          <span className="text-sm font-mono text-gray-500">{color}</span>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit" size="small">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={!name.trim() || isPending}
          sx={{
            bgcolor: "#c00035",
            "&:hover": { bgcolor: "#a0002b" },
            textTransform: "none",
          }}
        >
          {isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Category tab with kebab menu
// ---------------------------------------------------------------------------

interface CategoryTabProps {
  cat: UserCustomAppCategory;
  count: number;
  isActive: boolean;
  onClick: () => void;
  onRename: () => void;
  onDelete: () => void;
}

const CategoryTab: React.FC<CategoryTabProps> = ({
  cat,
  count,
  isActive,
  onClick,
  onRename,
  onDelete,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = () => setMenuAnchor(null);

  return (
    <div className="group/tab flex-shrink-0 inline-flex items-center relative">
      {/* Tab button */}
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 pl-4 pr-1 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all duration-150 ${
          isActive
            ? "border-primary-500 text-primary-600"
            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
        }`}
      >
        {cat.color && (
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: cat.color }}
          />
        )}
        {cat.name}
        <span
          className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
            isActive
              ? "bg-primary-100 text-primary-600"
              : "bg-gray-100 text-gray-400"
          }`}
        >
          {count}
        </span>
      </button>

      {/* Kebab — visible on hover or when menu is open */}
      <button
        type="button"
        onClick={openMenu}
        title="Category options"
        className={`rounded transition-all overflow-hidden flex items-center justify-center text-gray-400 hover:text-gray-600 ${
          menuAnchor
            ? "w-5 opacity-100"
            : "w-0 opacity-0 group-hover/tab:w-5 group-hover/tab:opacity-100"
        }`}
      >
        <MoreHorizontal size={13} />
      </button>

      {/* MUI Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { minWidth: 140, boxShadow: 3, borderRadius: 2 } },
        }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            onRename();
          }}
          dense
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 13 }}>
            Rename
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            onDelete();
          }}
          dense
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: "error.main" }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 13 }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export interface MyAppsSectionProps {
  searchQuery: string;
}

export const MyAppsSection: React.FC<MyAppsSectionProps> = ({
  searchQuery,
}) => {
  const [activeTab, setActiveTab] = useState<number | "all">("all");
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<UserCustomAppCategory | null>(null);
  const [categoryToDelete, setCategoryToDelete] =
    useState<UserCustomAppCategory | null>(null);
  const [activeModal, setActiveModal] = useState<{
    type: "add" | "edit";
    app?: UserCustomApp;
  } | null>(null);
  const [appToDelete, setAppToDelete] = useState<UserCustomApp | null>(null);

  const { data: customApps = [], isLoading } = useMyCustomApps();
  const { data: categories = [] } = useMyCategories();
  const createMutation = useCreateCustomApp();
  const updateMutation = useUpdateCustomApp();
  const deleteMutation = useDeleteCustomApp();
  const deleteCategoryMutation = useDeleteCategory();
  const _toggleFavoriteMutation = useToggleCustomAppFavorite();
  const reorderMutation = useReorderCustomApps();

  // Filter by search, then by active tab
  const displayedApps = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let result = customApps;
    if (q) {
      result = result.filter(
        (app) =>
          app.name.toLowerCase().includes(q) ||
          (app.description && app.description.toLowerCase().includes(q)),
      );
    }
    if (activeTab !== "all") {
      result = result.filter((app) => app.categoryId === activeTab);
    }
    return result;
  }, [customApps, searchQuery, activeTab]);

  const handleReorder = (activeId: number, overId: number) => {
    const fullIds = customApps.map((app) => app.id);
    const visibleIds = displayedApps.map((app) => app.id);
    const nextIds = reorderVisibleApps(fullIds, visibleIds, activeId, overId);
    reorderMutation.mutate(sortAppsByOrder(customApps, nextIds));
  };

  const handleSaveApp = (dto: CreateUserCustomAppDto) => {
    if (activeModal?.type === "edit" && activeModal.app) {
      updateMutation.mutate({ id: activeModal.app.id, data: dto });
    } else {
      createMutation.mutate(dto);
    }
    setActiveModal(null);
  };

  const handleConfirmDeleteApp = () => {
    if (appToDelete) {
      deleteMutation.mutate(appToDelete.id);
      setAppToDelete(null);
    }
  };

  const handleConfirmDeleteCategory = () => {
    if (categoryToDelete) {
      deleteCategoryMutation.mutate(categoryToDelete.id, {
        onSuccess: () => {
          if (activeTab === categoryToDelete.id) setActiveTab("all");
          setCategoryToDelete(null);
        },
      });
    }
  };

  const handleCategoryCreated = (cat: UserCustomAppCategory) => {
    setShowCreateCategory(false);
    setActiveTab(cat.id);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const sortedCategories = [...categories].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  return (
    <>
      <section className="mt-10">
        {/* Title */}
        <h2 className="text-sm font-semibold text-gray-700 mb-2">
          Custom Applications
        </h2>

        {/* Tab bar + Add App button */}
        <div className="flex items-center border-b border-gray-200 mb-6">
          <div className="flex items-center gap-1 flex-1 overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {/* All tab */}
            <button
              type="button"
              onClick={() => setActiveTab("all")}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all duration-150 ${
                activeTab === "all"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              All
              <span
                className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
                  activeTab === "all"
                    ? "bg-primary-100 text-primary-600"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {customApps.length}
              </span>
            </button>

            {/* Category tabs */}
            {sortedCategories.map((cat) => (
              <CategoryTab
                key={cat.id}
                cat={cat}
                count={customApps.filter((a) => a.categoryId === cat.id).length}
                isActive={activeTab === cat.id}
                onClick={() => setActiveTab(cat.id)}
                onRename={() => setEditingCategory(cat)}
                onDelete={() => setCategoryToDelete(cat)}
              />
            ))}

            {/* Add category button */}
            <button
              type="button"
              onClick={() => setShowCreateCategory(true)}
              title="Add category"
              className="flex-shrink-0 inline-flex items-center px-3 py-2.5 text-gray-400 hover:text-primary-600 transition-colors border-b-2 border-transparent -mb-px"
            >
              <Plus size={14} />
            </button>
          </div>

          {/* Add App button */}
          <button
            type="button"
            onClick={() => setActiveModal({ type: "add" })}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 ml-3 rounded-lg text-xs font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors"
          >
            <Plus size={14} />
            Add App
          </button>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-[150px] bg-gray-100 rounded-b-xl animate-pulse"
              />
            ))}
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {displayedApps.length === 0 ? (
              <motion.div
                key={`empty-${activeTab}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="flex flex-col items-center justify-center py-10 bg-white rounded-xl border border-dashed border-gray-200 shadow-sm">
                  <p className="text-gray-500 text-sm font-medium mb-1">
                    No apps found
                  </p>
                  <p className="text-gray-400 text-xs mb-4">
                    {activeTab === "all"
                      ? "Add your first custom external link"
                      : "No apps in this category yet"}
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveModal({ type: "add" })}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all text-xs font-semibold shadow-md shadow-primary-500/20"
                  >
                    <Plus size={14} />
                    Add App
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={`grid-${activeTab}`}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <CustomAppsGrid
                  apps={displayedApps}
                  sortable={!searchQuery.trim()}
                  onReorder={handleReorder}
                  onEdit={(app) => setActiveModal({ type: "edit", app })}
                  onDelete={(app) => setAppToDelete(app)}
                />
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </section>

      {/* Create Category */}
      <CategoryFormModal
        open={showCreateCategory}
        onClose={() => setShowCreateCategory(false)}
        onCreated={handleCategoryCreated}
      />

      {/* Rename Category */}
      <CategoryFormModal
        open={!!editingCategory}
        onClose={() => setEditingCategory(null)}
        editCategory={editingCategory}
      />

      {/* Delete Category Confirmation */}
      <ConfirmModal
        isOpen={!!categoryToDelete}
        onClose={() => setCategoryToDelete(null)}
        onConfirm={handleConfirmDeleteCategory}
        title="Delete Category"
        message={`Delete "${categoryToDelete?.name}"? Apps in this category will become uncategorized.`}
        variant="danger"
        confirmText="Delete"
        isLoading={deleteCategoryMutation.isPending}
      />

      {/* Add/Edit App */}
      <AddEditCustomAppModal
        isOpen={!!activeModal}
        onClose={() => setActiveModal(null)}
        onSubmit={handleSaveApp}
        isLoading={isSaving}
        editApp={activeModal?.app || null}
      />

      {/* Delete App Confirmation */}
      <ConfirmModal
        isOpen={!!appToDelete}
        onClose={() => setAppToDelete(null)}
        onConfirm={handleConfirmDeleteApp}
        title="Delete App"
        message={`Are you sure you want to remove "${appToDelete?.name}"?`}
        variant="danger"
        confirmText="Delete"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default MyAppsSection;
